// The one message box for rooms and threads: a pill holding ＋ and the text, and a round send button beside
// it, the shape every phone user already knows. The send button is grey while the box is empty and accent
// once there is something to send. Screens keep their own rows above it (reply preview, typing, mentions).
import React from 'react';
import { View, Pressable, ActivityIndicator, StyleSheet, Platform, TextInputProps } from 'react-native';
import { Text, TextInput } from './Text';
import { makeStyles, useTheme } from '../theme';

export function Composer({ value, onChangeText, onSend, placeholder, onAttach, attaching, sending, inputProps }: {
  value: string; onChangeText: (t: string) => void; onSend: () => void; placeholder: string;
  /** Shows the ＋ inside the pill (photo, camera, file). Leave out where attachments make no sense. */
  onAttach?: () => void; attaching?: boolean; sending?: boolean; inputProps?: TextInputProps;
}) {
  const theme = useTheme(); const s = useStyles();
  const ready = !!value.trim() && !sending;
  // Web with a real keyboard: Enter sends, Shift+Enter starts a new line, like every desktop chat.
  // (On phones the return key already sends: submitBehavior below. The browser ignores that prop, so a
  // desktop user had to reach for the mouse.) A touch keyboard in a browser keeps Enter as a new line.
  const onKeyPress: TextInputProps['onKeyPress'] = Platform.OS !== 'web' ? inputProps?.onKeyPress : (e) => {
    inputProps?.onKeyPress?.(e);
    const k = e.nativeEvent as unknown as { key?: string; shiftKey?: boolean; ctrlKey?: boolean; altKey?: boolean; metaKey?: boolean; isComposing?: boolean };
    if (k.key !== 'Enter' || k.shiftKey || k.altKey || k.isComposing || touchKeyboard()) return;
    e.preventDefault(); // no new line in the box
    if (ready) onSend();
  };
  return (
    <View style={s.bar}>
      <View style={s.pill}>
        {onAttach && (
          <Pressable onPress={onAttach} disabled={attaching} hitSlop={6} style={({ pressed }) => [s.plus, pressed && { opacity: 0.5 }]} accessibilityRole="button" accessibilityLabel="Attach">
            {attaching ? <ActivityIndicator color={theme.muted} size="small" /> : <Text style={s.plusText}>＋</Text>}
          </Pressable>
        )}
        <TextInput
          style={[s.input, !onAttach && { paddingLeft: 12 }]} value={value} onChangeText={onChangeText}
          placeholder={placeholder} placeholderTextColor={theme.faint}
          multiline returnKeyType="send" onSubmitEditing={onSend} submitBehavior="submit"
          textAlignVertical="center" underlineColorAndroid="transparent"
          {...(Platform.OS === 'web' ? ({ rows: 1 } as any) : {})} // browsers default a textarea to two rows; phones size to the text
          {...inputProps}
          onKeyPress={onKeyPress}
        />
      </View>
      <Pressable onPress={onSend} disabled={!ready} style={({ pressed }) => [s.send, ready ? s.sendReady : s.sendIdle, pressed && ready && { opacity: 0.7 }]}
        accessibilityRole="button" accessibilityLabel="Send" accessibilityState={{ disabled: !ready }}>
        {sending ? <ActivityIndicator color={theme.accentInk} size="small" /> : <Text style={[s.sendGlyph, { color: ready ? theme.accentInk : theme.faint }]}>↑</Text>}
      </Pressable>
    </View>
  );
}

/** A phone or tablet browser: the main pointer is a finger, so the keyboard is on the screen. */
const touchKeyboard = () => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(pointer: coarse)').matches;

const useStyles = makeStyles((theme) => StyleSheet.create({
  bar: { flexDirection: 'row', alignItems: 'flex-end', gap: 8, paddingHorizontal: 10, paddingVertical: 8, borderTopWidth: 1, borderTopColor: theme.border, backgroundColor: theme.bg },
  // The pill grows with the text; ＋ stays on the bottom line, like Messages and WhatsApp.
  pill: { flex: 1, flexDirection: 'row', alignItems: 'flex-end', minHeight: 44, backgroundColor: theme.surface, borderWidth: 1, borderColor: theme.border, borderRadius: 22, paddingLeft: 4, paddingRight: 14 },
  plus: { width: 36, height: 42, alignItems: 'center', justifyContent: 'center' },
  plusText: { color: theme.muted, fontSize: 24, lineHeight: 26 },
  // iOS: 20 line + 11+11 padding fills the 42 inner height exactly. Android: textAlignVertical centres the text.
  input: { flex: 1, minHeight: 42, maxHeight: 120, color: theme.ink, fontSize: 16, lineHeight: 20, paddingTop: Platform.OS === 'ios' ? 11 : 9, paddingBottom: Platform.OS === 'ios' ? 11 : 9, paddingHorizontal: 4, includeFontPadding: false },
  send: { width: 44, height: 44, borderRadius: 22, alignItems: 'center', justifyContent: 'center' },
  sendReady: { backgroundColor: theme.accent },
  sendIdle: { backgroundColor: theme.surface2, borderWidth: 1, borderColor: theme.border },
  sendGlyph: { fontSize: 22, fontWeight: '800', lineHeight: 24 },
}));
